import React from 'react';
import {
  CreditCard,
  Receipt,
  CheckCircle2,
  AlertTriangle,
  FileWarning,
  HelpCircle,
  CornerUpLeft,
  Coins,
  ShieldCheck,
  Ban,
  Layers,
} from 'lucide-react';
import { ReconciliationSummary } from '../../types/reconciliation';

interface KpiGridProps {
  summary: ReconciliationSummary;
  onFilterStatus?: (status: string) => void;
}

export const KpiGrid: React.FC<KpiGridProps> = ({ summary, onFilterStatus }) => {
  const formatCurrency = (amount: number) => {
    return '₹' + amount.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  const cards = [
    // Core Counts
    {
      title: 'Total Razorpay Txns',
      value: summary.totalRazorpayRecords.toLocaleString('en-IN'),
      subtitle: `Captured in gateway`,
      icon: CreditCard,
      color: 'text-brand-600 dark:text-brand-400',
      bg: 'bg-brand-50 dark:bg-brand-950/40 border-brand-200 dark:border-brand-800',
    },
    {
      title: 'Total ERP Bills',
      value: summary.totalErpRecords.toLocaleString('en-IN'),
      subtitle: `Grouped into ${summary.totalGroups} payment IDs`,
      icon: Receipt,
      color: 'text-slate-700 dark:text-slate-300',
      bg: 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700',
    },
    {
      title: 'Exact Matches',
      value: summary.exactMatches.toLocaleString('en-IN'),
      subtitle: `${summary.matchPercentage}% of all payment groups`,
      icon: CheckCircle2,
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-50/70 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800',
      statusFilter: 'Exact Match',
    },
    {
      title: 'Partial Matches',
      value: summary.partialMatches.toLocaleString('en-IN'),
      subtitle: 'Razorpay > ERP Total (Under-billed)',
      icon: AlertTriangle,
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-50/70 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      statusFilter: 'Partial Match',
    },
    {
      title: 'Over-Billed Cases',
      value: summary.overBilled.toLocaleString('en-IN'),
      subtitle: 'ERP Total > Razorpay (Over-invoiced)',
      icon: Layers,
      color: 'text-purple-600 dark:text-purple-400',
      bg: 'bg-purple-50/70 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800',
      statusFilter: 'Over Billed',
    },
    {
      title: 'Missing ERP Bills',
      value: summary.noBillReference.toLocaleString('en-IN'),
      subtitle: 'In Razorpay, zero ERP bills',
      icon: HelpCircle,
      color: 'text-rose-600 dark:text-rose-400',
      bg: 'bg-rose-50/70 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800',
      statusFilter: 'No Bill Reference',
    },
    {
      title: 'ERP Only Records',
      value: summary.erpOnly.toLocaleString('en-IN'),
      subtitle: 'In ERP, missing Razorpay payment',
      icon: Ban,
      color: 'text-indigo-600 dark:text-indigo-400',
      bg: 'bg-indigo-50/70 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800',
      statusFilter: 'ERP Only',
    },
    {
      title: 'Refund Transactions',
      value: summary.refundedCount.toLocaleString('en-IN'),
      subtitle: 'Refunded via Razorpay gateway',
      icon: CornerUpLeft,
      color: 'text-sky-600 dark:text-sky-400',
      bg: 'bg-sky-50/70 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800',
      statusFilter: 'Refunded',
    },

    // Financial Values (Highlighted Cards)
    {
      title: 'Total Collections',
      value: formatCurrency(summary.totalCollectionAmount),
      subtitle: 'Net Razorpay customer payments',
      icon: Coins,
      color: 'text-brand-700 dark:text-brand-300 font-bold',
      bg: 'bg-gradient-to-br from-brand-50 to-blue-50/80 dark:from-brand-950/50 dark:to-slate-900 border-brand-300 dark:border-brand-700 shadow-sm',
    },
    {
      title: 'Total Reconciled',
      value: formatCurrency(summary.totalReconciledAmount),
      subtitle: 'Cleared with 100% concordance',
      icon: ShieldCheck,
      color: 'text-emerald-700 dark:text-emerald-300 font-bold',
      bg: 'bg-gradient-to-br from-emerald-50 to-teal-50/80 dark:from-emerald-950/50 dark:to-slate-900 border-emerald-300 dark:border-emerald-700 shadow-sm',
    },
    {
      title: 'Total Exceptions',
      value: formatCurrency(summary.totalExceptionAmount),
      subtitle: 'Variances, missing bills & refunds',
      icon: FileWarning,
      color: 'text-rose-700 dark:text-rose-300 font-bold',
      bg: 'bg-gradient-to-br from-rose-50 to-amber-50/80 dark:from-rose-950/50 dark:to-slate-900 border-rose-300 dark:border-rose-700 shadow-sm',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3.5">
      {cards.map((card, idx) => {
        const Icon = card.icon;
        const isInteractive = !!card.statusFilter && !!onFilterStatus;

        return (
          <div
            key={idx}
            onClick={() => isInteractive && onFilterStatus!(card.statusFilter!)}
            className={`p-4 rounded-xl border transition duration-150 ${card.bg} ${
              isInteractive ? 'cursor-pointer hover:shadow-md hover:scale-[1.01]' : ''
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[12px] font-medium text-slate-500 dark:text-slate-400">
                  {card.title}
                </span>
                <div className={`mt-1.5 text-xl font-bold tracking-tight text-slate-900 dark:text-white`}>
                  {card.value}
                </div>
              </div>
              <div className={`p-2 rounded-lg bg-white/80 dark:bg-slate-800/80 shadow-xs ${card.color}`}>
                <Icon className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 truncate">
              {card.subtitle}
            </div>
          </div>
        );
      })}
    </div>
  );
};
